import {
  CloseRounded,
  ExitToAppRounded,
  FavoriteBorder,
  FeedbackRounded,
  HelpOutlineRounded,
  HelpRounded,
  KeyboardArrowRightRounded,
  RefreshRounded,
  SpeedOutlined,
  VpnKey
} from '@mui/icons-material'
import MenuIcon from '@mui/icons-material/Menu'
import { Box, Divider, Menu, MenuItem, Tooltip, Typography } from '@mui/material'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import drvLogo from '../../../../assets/Images/drv/drvLogo.svg'
import { logout } from '../../../../redux/actions/auth'
import { IRootState } from '../../../../redux/types'
import ChangeUserPassword from '../../../backoffice/auth/ui/ChangeUserPassword'
import { ROLES } from '../../../backoffice/users/models/user'
import {
  AvatarButton,
  AvatarMobile,
  BoxProductBy,
  ButtonBrand,
  CloseDrawerButton,
  DrawerPaper,
  DrvLogo
} from '../../styles/AccountMenuStyles'
import ButtonNavbar from '../../styles/ButtonNavbar'
import HiddenDesktop from '../../styles/HiddenDesktop'
import HiddenMobile from '../../styles/HiddenMobile'
import MenuMobileItem from '../../styles/MenuMobileItem'
import { clearStorage } from '../../utils/ClearStorage'
import ShareIcon from '../icons/ShareIcon'
import GlobalSearch from '../inputs/GlobalSearch'
import NotificationPanel from './NotificationPanel'

function AccountMenu({ theme }: { theme: string }) {
  const { t } = useTranslation(['global'])
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { user: authUser } = useSelector((state: IRootState) => state.auth)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(anchorEl)
  const [userPassword, setUserPassword] = useState(false)
  const [mobileUserOpen, setMobileUserOpen] = useState(false)

  const handleProfileMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseUserMenuMobile = () => {
    if (mobileUserOpen) setMobileUserOpen(false)
  }

  const handleProfileUser = () => {
    if (theme === 'client') navigate('/perfil')
    else navigate('/usuarios/perfil')
  }

  let userName = authUser !== null ? authUser.name.split(' ') : import.meta.env.VITE_NAME
  let countWords = userName.length
  let textAvatar =
    countWords <= 1
      ? `${userName[0].charAt(0).toLocaleUpperCase()}`
      : ['(C)', '(Cc)'].includes(userName[0])
        ? `${userName[1].charAt(0).toLocaleUpperCase()}`
        : `${userName[0].charAt(0).toLocaleUpperCase()}${userName[1].charAt(0).toLocaleUpperCase()}`

  return (
    <>
      <HiddenMobile display="flex" alignItems="center">
        <Tooltip title={`${t('help')}`}>
          <ButtonNavbar className={theme} onClick={() => navigate('/ayuda')} id="help-button">
            <HelpRounded />
          </ButtonNavbar>
        </Tooltip>
        {theme === 'client' && <NotificationPanel theme={theme} />}
        <Tooltip title={`${t('your-profile-and-options')}`}>
          <ButtonNavbar
            className={theme}
            // aria-controls="primary-search-account-menu"
            // aria-haspopup="true"
            // underline="none"
            id="user-avatar-button"
            onClick={handleProfileMenuOpen}
          >
            <AvatarButton>{textAvatar}</AvatarButton>
          </ButtonNavbar>
        </Tooltip>
        {/* <img src={`${drvLogo}`} alt='drv logo' className={clsx(classes.drvLogo, theme === 'client' ? '' : 'admin')} /> */}
        <Menu
          anchorEl={anchorEl}
          id="primary-search-account-menu"
          keepMounted
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={isMenuOpen}
          onClose={() => setAnchorEl(null)}
          onClick={() => setAnchorEl(null)}
          data-testid="user-menu"
        >
          {window.location.pathname !== '/terms' && (
            <div>
              <Box px={2} pb={1}>
                <Typography variant="caption" color="textSecondary">
                  {authUser !== null ? authUser.name : import.meta.env.VITE_NAME}
                </Typography>
              </Box>
              {authUser != null && ![ROLES.client, ROLES.trainer].includes(authUser.role_id) && (
                <>
                  <Divider />
                  {theme === 'client' ? (
                    <MenuItem component="a" href="/escritorio">
                      {import.meta.env.VITE_NAME} | Admin
                    </MenuItem>
                  ) : (
                    <MenuItem component="a" href="/inicio">
                      {import.meta.env.VITE_NAME} | Cliente
                    </MenuItem>
                  )}
                </>
              )}
              <MenuItem onClick={() => handleProfileUser()}>{t('profile')}</MenuItem>
              <MenuItem component="a" href="/favoritos" data-testid="menu-favorites-option">
                {t('my-favorites')}
              </MenuItem>
              <MenuItem data-testid="change-password-option" onClick={() => setUserPassword(!userPassword)}>
                {t('login.change-password')}
              </MenuItem>
            </div>
          )}
          <MenuItem onClick={() => dispatch(logout())}>{t('login.logout')}</MenuItem>
          <Box display="flex" justifyContent="center" height={24}>
            <DrvLogo src={`${drvLogo}`} alt="drv logo" className={theme === 'client' ? '' : 'admin'} />
          </Box>
        </Menu>
      </HiddenMobile>

      <HiddenDesktop>
        <Box display="flex" sx={{ width: '120%' }}>
          {theme === 'client' && <NotificationPanel theme={theme} />}
          <GlobalSearch />
          <ButtonBrand
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            underline="none"
            style={{ padding: '0px 16px' }}
            onClick={() => setMobileUserOpen(!mobileUserOpen)}
          >
            <Box gap={1} alignItems="center" display="flex">
              {/* <AccountCircleRounded color="primary" /> */}
              <MenuIcon fontSize="small" />
            </Box>
          </ButtonBrand>
          <DrawerPaper
            variant="temporary"
            anchor="right"
            open={mobileUserOpen}
            onClose={() => setMobileUserOpen(!mobileUserOpen)}
            ModalProps={{ keepMounted: true }}
          >
            <CloseDrawerButton onClick={() => setMobileUserOpen(!mobileUserOpen)}>
              <CloseRounded />
            </CloseDrawerButton>
            <Box display="inline" onClick={handleCloseUserMenuMobile} overflow={'auto'} pb={8}>
              <Box display="flex" mx="12px" mt="68px" mb={3} justifyContent="space-between" onClick={handleProfileUser}>
                <Box display="flex" alignItems="start">
                  <AvatarMobile>{textAvatar}</AvatarMobile>
                  <Box display="inline">
                    <Typography variant="body2">{userName}</Typography>
                    <Typography variant="body2">ID: {authUser.id}</Typography>
                    <Typography style={{ color: '#3483FA' }} variant="body2">
                      {t('common.manage-my-account')} {import.meta.env.VITE_NAME}
                    </Typography>
                  </Box>
                </Box>
                <Box p="13px">
                  {' '}
                  <KeyboardArrowRightRounded />
                </Box>
              </Box>
              {![ROLES.client, ROLES.trainer].includes(authUser?.role_id) && (
                <>
                  {theme === 'client' ? (
                    <MenuMobileItem classes={{ root: theme }} onClick={() => navigate('/escritorio')}>
                      <SpeedOutlined />
                      {t('common.change-to')} {t('common.admin')}
                    </MenuMobileItem>
                  ) : (
                    <MenuMobileItem classes={{ root: theme }} onClick={() => navigate('/inicio')}>
                      <SpeedOutlined />
                      {t('common.change-to')} {t('common.client')}
                    </MenuMobileItem>
                  )}
                  <Divider style={{ marginTop: 12, marginBottom: 12 }} />
                </>
              )}

              <MenuMobileItem classes={{ root: 'client' }} onClick={() => navigate('/favoritos')}>
                <FavoriteBorder />
                {t('my-favorites')}
              </MenuMobileItem>
              <ShareIcon
                component="menuItem"
                light={true}
                type="resource"
                visibility={0}
                title={'Bilbioteca'}
                description={'Biblioteca'}
                link={window.location.href}
                multimedia={``}
              />
              <MenuMobileItem classes={{ root: 'client' }} onClick={() => setUserPassword(!userPassword)}>
                <VpnKey />
                {t('login.change-password')}
              </MenuMobileItem>
              <Divider style={{ marginTop: 12, marginBottom: 12 }} />
              <MenuMobileItem classes={{ root: 'client' }} onClick={clearStorage}>
                <RefreshRounded />
                {t('restart')}
              </MenuMobileItem>
              <MenuMobileItem classes={{ root: 'client' }} onClick={() => window.open('https://drv.es/reportar-error-en-plei/')}>
                <FeedbackRounded />
                {t('Reportar')}
              </MenuMobileItem>
              <MenuMobileItem classes={{ root: 'client' }} onClick={() => navigate('/ayuda')}>
                <HelpOutlineRounded />
                {t('help')}
              </MenuMobileItem>
              <MenuMobileItem classes={{ root: 'client' }} onClick={() => dispatch(logout())}>
                <ExitToAppRounded />
                {t('login.logout')}
              </MenuMobileItem>
            </Box>
            <BoxProductBy>
              <Typography variant="caption"> {t('common.a-product-of')}</Typography>
              <img src={String(drvLogo)} alt={`Logo de ${import.meta.env.VITE_NAME}`} height={18} />
            </BoxProductBy>
          </DrawerPaper>
        </Box>
      </HiddenDesktop>

      <ChangeUserPassword showForm={userPassword} setShowForm={() => setUserPassword(!userPassword)} />
    </>
  )
}

export default AccountMenu
